/* eslint-disable react/no-unknown-property */
import { BlofinProvider } from '@blockfin/blockfin-component';
import { ThemeProvider } from '@blofin/blofin-ui';
import useRates from 'hooks/useRates';
import useUser from 'hooks/useUser';
import { NextIntlClientProvider } from 'next-intl';
import type { AppProps } from 'next/app';
import localFont from 'next/font/local';
import Head from 'next/head';
import { useEffect, useMemo } from 'react';
import axios from 'services/http';
import 'src/styles/globals.css';
import { DefaultSeo } from 'next-seo';
import SEO from 'config/next-seo.config';
import { useRouter } from 'next/router';
import { domain } from 'config/google';
import { getHerfConfig } from 'utils/helper';
import React from 'react';
import ReactDOM from 'react-dom';
import getConfig from 'next/config';

const HarmonyOS = localFont({
  src: [
    {
      path: '../fonts/HarmonyOS_Sans_Light.woff2',
      weight: '300',
      style: 'normal',
    },
    {
      path: '../fonts/HarmonyOS_Sans_Regular.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: '../fonts/HarmonyOS_Sans_Medium.woff2',
      weight: '500',
      style: 'normal',
    },
    {
      path: '../fonts/HarmonyOS_Sans_Black.woff2',
      weight: '600',
      style: 'normal',
    },
    {
      path: '../fonts/HarmonyOS_Sans_Bold.woff2',
      weight: '700',
      style: 'normal',
    },
  ],
});

const getCanonicalurl = (locale: string, basePath: string, path: string) => {
  path = path.split('?')[0];
  return `${domain}${basePath}${locale === 'en' ? '' : `/${locale}`}${path === '/' ? '' : path}`;
};

function MyApp({ Component, pageProps }: AppProps) {
  const { userInfo } = useUser();

  const { rates } = useRates();
  const router = useRouter();

  const data = useMemo(() => {
    const { publicRuntimeConfig } = getConfig();
    return {
      userInfo: userInfo,
      baseUrl: pageProps?.isDemoTrading === 2 ? publicRuntimeConfig.base.demoUrl : publicRuntimeConfig.base.baseUrl,
      http: axios,
      rates: rates,
      isDemoTrading: pageProps?.isDemoTrading > 0,
      messages: pageProps.messages,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, rates, pageProps?.isDemoTrading]);

  useEffect(() => {
    window.React = React;
    window.ReactDOM = ReactDOM;
  }, []);

  useEffect(() => {
    if (userInfo !== null) {
      window.userInfo = userInfo;
    }
  }, [userInfo]);

  return (
    <>
      <style jsx global>{`
        html {
          font-family: ${HarmonyOS.style.fontFamily};
        }
      `}</style>
      <DefaultSeo
        {...SEO}
        canonical={getCanonicalurl(router.locale as string, router.basePath, router.asPath)}
        languageAlternates={getHerfConfig(router)}
      />
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0" />
      </Head>
      <ThemeProvider value={{ theme: pageProps.theme || 'light' }}>
        <BlofinProvider value={data}>
          <NextIntlClientProvider locale={router.locale} messages={pageProps.messages}>
            <Component {...pageProps} />
          </NextIntlClientProvider>
        </BlofinProvider>
      </ThemeProvider>
    </>
  );
}

export default MyApp;
